import { defineStore } from "pinia";
import { ref } from "vue";
import { createTableComment, updateTableComment, deleteTableComment } from '@/api/endpoints/tableComments';
import { useNotificationsStore } from '@/store/notificationsStore';

// MERK: Inngangs-payload kan komme fra legacy-stier (PascalCase: RowId, ColumnName,
// CommentId, Comment, TableKey) eller moderne stier (camelCase). Dette er ikke
// fallback til et annet logisk felt, men ren case-normalisering — samme verdi
// uansett kilde. Se WI #1279 audit: dette mønsteret er hvitlistet og krever ikke
// /precommit-rensing før BE-kontrakten standardiseres på camelCase.

function resolveTableKey (payload) {
  const value = payload?.tableKey ?? payload?.TableKey;
  const tableKey = String(value ?? '').trim();
  return tableKey || null;
}

export const useCommentStore = defineStore("commentStore", () => {
  const isCreating = ref(false);
  const isUpdating = ref(false);
  const isDeleting = ref(false);
  const notificationsStore = useNotificationsStore();

  function notifyError (title, error) {
    const problem = error?.response?.data;
    if (problem?.title) {
      notificationsStore.addProblem(problem);
      return;
    }
    notificationsStore.addError(title, error?.message || 'Ukjent feil');
  }

  async function createComment (payload) {
    isCreating.value = true;
    try {
      const tableKey = resolveTableKey(payload);
      if (!tableKey) {
        notificationsStore.addError('Kunne ikke opprette kommentar', 'tableKey mangler for kommentaroperasjon.');
        return null;
      }
      const data = await createTableComment(tableKey, {
        rowId: Number(payload.RowId ?? payload.rowId),
        columnKey: payload.ColumnName ?? payload.columnName,
        comment: payload.Comment ?? payload.comment
      });
      return { data };
    } catch (error) {
      notifyError('Kunne ikke opprette kommentar', error);
      return null;
    } finally {
      isCreating.value = false;
    }
  }

  async function updateComment (payload) {
    isUpdating.value = true;
    try {
      const tableKey = resolveTableKey(payload);
      if (!tableKey) {
        notificationsStore.addError('Kunne ikke oppdatere kommentar', 'tableKey mangler for kommentaroperasjon.');
        return null;
      }
      const data = await updateTableComment(tableKey, {
        rowId: Number(payload.RowId ?? payload.rowId),
        columnKey: payload.ColumnName ?? payload.columnName,
        commentId: String(payload.CommentId ?? payload.commentId),
        comment: payload.Comment ?? payload.comment
      });
      return { data };
    } catch (error) {
      notifyError('Kunne ikke oppdatere kommentar', error);
      return null;
    } finally {
      isUpdating.value = false;
    }
  }

  async function deleteComment (payload) {
    isDeleting.value = true;
    try {
      const tableKey = resolveTableKey(payload);
      if (!tableKey) {
        notificationsStore.addError('Kunne ikke slette kommentar', 'tableKey mangler for kommentaroperasjon.');
        return null;
      }
      const data = await deleteTableComment(tableKey, {
        rowId: Number(payload.RowId ?? payload.rowId),
        columnKey: payload.ColumnName ?? payload.columnName,
        commentId: String(payload.CommentId ?? payload.commentId)
      });
      return { data };
    } catch (error) {
      notifyError('Kunne ikke slette kommentar', error);
      return null;
    } finally {
      isDeleting.value = false;
    }
  }

  return {
    isCreating,
    isUpdating,
    isDeleting,
    createComment,
    updateComment,
    deleteComment
  };
});
